/**
 * Aggregations and insights for time-based performance analytics.
 * Inputs: completed trade rows { pnl, status, tradeDate, symbol?, ... }
 */

export const OVERTRADING_THRESHOLD = 5;
export const MIN_TRADES_FOR_LOW_SAMPLE_WARNING = 12;
export const MIN_TRADES_PER_SEGMENT_FOR_MID_MONTH = 3;
export const INSIGHT_MAX_COUNT = 5;

const WEEKDAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKDAY_ORDER = [1, 2, 3, 4, 5]; // getDay(): Mon=1 … Fri=5

export function getTradeDateString(trade) {
  const d = trade.tradeDate || trade.date;
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseTradeDate(trade) {
  const s = getTradeDateString(trade);
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

/** Mon–Fri rows: Day, totals, win rate */
export function aggregateDayOfWeek(completedTrades) {
  const buckets = new Map();
  for (const idx of WEEKDAY_ORDER) {
    buckets.set(idx, { totalTrades: 0, totalPnl: 0, wins: 0, losses: 0, breakEven: 0 });
  }

  for (const t of completedTrades) {
    const dt = parseTradeDate(t);
    if (!dt) continue;
    const dow = dt.getDay();
    if (dow < 1 || dow > 5) continue;
    const b = buckets.get(dow);
    b.totalTrades += 1;
    b.totalPnl += Number(t.pnl) || 0;
    const st = String(t.status || '').toUpperCase();
    if (st === 'WIN') b.wins += 1;
    else if (st === 'LOSS') b.losses += 1;
    else b.breakEven += 1;
  }

  return WEEKDAY_ORDER.map((dayIndex, i) => {
    const b = buckets.get(dayIndex);
    const denom = b.wins + b.losses + b.breakEven;
    const winRate = denom > 0 ? (b.wins / denom) * 100 : 0;
    return {
      dayIndex,
      label: WEEKDAY_LABELS[i],
      totalTrades: b.totalTrades,
      totalPnl: Math.round(b.totalPnl * 100) / 100,
      wins: b.wins,
      losses: b.losses,
      breakEven: b.breakEven,
      winRate
    };
  });
}

/** Calendar day-of-month 1–31 (all months combined). */
export function aggregateDayOfMonth(completedTrades) {
  const map = new Map();
  for (let dom = 1; dom <= 31; dom++) {
    map.set(dom, { pnl: 0, trades: 0, wins: 0, losses: 0, breakEven: 0 });
  }
  for (const t of completedTrades) {
    const dt = parseTradeDate(t);
    if (!dt) continue;
    const dom = dt.getDate();
    if (dom < 1 || dom > 31) continue;
    const b = map.get(dom);
    b.trades += 1;
    b.pnl += Number(t.pnl) || 0;
    const st = String(t.status || '').toUpperCase();
    if (st === 'WIN') b.wins += 1;
    else if (st === 'LOSS') b.losses += 1;
    else b.breakEven += 1;
  }
  for (const b of map.values()) {
    b.pnl = Math.round(b.pnl * 100) / 100;
  }
  return map;
}

export function sortTradesChronologically(trades) {
  return [...trades].sort((a, b) => {
    const sa = getTradeDateString(a) || '';
    const sb = getTradeDateString(b) || '';
    if (sa !== sb) return sa < sb ? -1 : sa > sb ? 1 : 0;
    const symA = String(a.symbol || '');
    const symB = String(b.symbol || '');
    if (symA !== symB) return symA < symB ? -1 : symA > symB ? 1 : 0;
    return 0;
  });
}

/**
 * Single source of truth for chronological ordering + stable index.
 * Reuse this wherever UI needs to align with streak indices.
 */
export function getChronologicallyIndexedTrades(trades) {
  return sortTradesChronologically(trades).map((trade, idx) => ({
    ...trade,
    chronologicalIdx: idx + 1
  }));
}

/** Max consecutive completed trades with pnl < 0 */
export function computeMaxLosingStreak(sortedTrades) {
  let cur = 0;
  let max = 0;
  for (const t of sortedTrades) {
    const pnl = Number(t.pnl) || 0;
    if (pnl < 0) {
      cur += 1;
      max = Math.max(max, cur);
    } else {
      cur = 0;
    }
  }
  return max;
}

/**
 * Returns losing streak proof details with index window and trade refs
 * based on the same streak logic used for insights (pnl < 0, consecutive).
 */
export function computeMaxLosingStreakDetails(sortedTrades) {
  let curStart = -1;
  let curLen = 0;
  let best = { length: 0, startPos: -1, endPos: -1 };

  for (let i = 0; i < sortedTrades.length; i++) {
    const pnl = Number(sortedTrades[i].pnl) || 0;
    if (pnl < 0) {
      if (curLen === 0) curStart = i;
      curLen += 1;
      if (curLen > best.length) {
        best = { length: curLen, startPos: curStart, endPos: i };
      }
    } else {
      curLen = 0;
      curStart = -1;
    }
  }

  if (best.length === 0) {
    return { length: 0, startIndex: -1, endIndex: -1, tradeRefs: [] };
  }

  const startTrade = sortedTrades[best.startPos];
  const endTrade = sortedTrades[best.endPos];
  const tradeRefs = sortedTrades.slice(best.startPos, best.endPos + 1).map((t) => ({
    localIndex: t.chronologicalIdx,
    symbol: t.symbol || '-',
    tradeDate: getTradeDateString(t),
    pnl: Number(t.pnl) || 0,
    status: String(t.status || '')
  }));

  return {
    length: best.length,
    startIndex: startTrade?.chronologicalIdx ?? -1,
    endIndex: endTrade?.chronologicalIdx ?? -1,
    tradeRefs
  };
}

export function aggregateByCalendarDay(completedTrades) {
  const map = new Map();
  for (const t of completedTrades) {
    const key = getTradeDateString(t);
    if (!key) continue;
    if (!map.has(key)) map.set(key, { count: 0, pnl: 0 });
    const b = map.get(key);
    b.count += 1;
    b.pnl += Number(t.pnl) || 0;
  }
  for (const b of map.values()) {
    b.pnl = Math.round(b.pnl * 100) / 100;
  }
  return map;
}

export function computeOvertradingMeta(completedTrades) {
  const byDay = aggregateByCalendarDay(completedTrades);
  const overDays = [];
  const normalDays = [];
  for (const [date, v] of byDay) {
    const row = { date, count: v.count, pnl: v.pnl };
    if (v.count > OVERTRADING_THRESHOLD) overDays.push(row);
    else normalDays.push(row);
  }
  const avgOver =
    overDays.length > 0 ? overDays.reduce((s, d) => s + d.pnl, 0) / overDays.length : null;
  const avgNormal =
    normalDays.length > 0 ? normalDays.reduce((s, d) => s + d.pnl, 0) / normalDays.length : null;
  return {
    overCount: overDays.length,
    avgOver,
    avgNormal,
    overDaysCount: overDays.length,
    normalDaysCount: normalDays.length,
    overDays,
    normalDays
  };
}

const SEGMENT_META = [
  { key: 'start', phraseStrong: 'at the start of the month', phraseWeak: 'At the start of the month', range: [1, 10] },
  { key: 'mid', phraseStrong: 'in mid-month', phraseWeak: 'Mid-month', range: [11, 20] },
  { key: 'end', phraseStrong: 'at the end of the month', phraseWeak: 'End of month', range: [21, 31] }
];

export function aggregateMidMonthSegments(completedTrades) {
  const init = () => ({ totalPnl: 0, totalTrades: 0, wins: 0, losses: 0, breakEven: 0 });
  const seg = { start: init(), mid: init(), end: init() };

  for (const t of completedTrades) {
    const dt = parseTradeDate(t);
    if (!dt) continue;
    const dom = dt.getDate();
    let key = 'start';
    if (dom >= 11 && dom <= 20) key = 'mid';
    else if (dom >= 21) key = 'end';

    const b = seg[key];
    b.totalTrades += 1;
    b.totalPnl += Number(t.pnl) || 0;
    const st = String(t.status || '').toUpperCase();
    if (st === 'WIN') b.wins += 1;
    else if (st === 'LOSS') b.losses += 1;
    else b.breakEven += 1;
  }

  return SEGMENT_META.map(meta => {
    const b = seg[meta.key];
    const denom = b.wins + b.losses + b.breakEven;
    const winRate = denom > 0 ? (b.wins / denom) * 100 : 0;
    return {
      key: meta.key,
      phraseStrong: meta.phraseStrong,
      phraseWeak: meta.phraseWeak,
      range: meta.range,
      totalPnl: Math.round(b.totalPnl * 100) / 100,
      totalTrades: b.totalTrades,
      wins: b.wins,
      losses: b.losses,
      breakEven: b.breakEven,
      winRate
    };
  });
}

function formatRupeePlain(n) {
  const v = Math.abs(n);
  return `${n >= 0 ? '+' : '-'}₹${v.toFixed(0)}`;
}

function buildInsight(id, type, title, summary, proof) {
  return { id, type, title, summary, proof };
}

/** Structured insights with proof payloads for hover rendering. */
export function generateTimeBasedInsightsDetailed(
  completedTrades,
  dayRows,
  losingStreakDetails,
  overMeta,
  midSegments
) {
  const out = [];
  const n = completedTrades.length;

  if (n === 0) {
    return [
      buildInsight('no-data', 'no_data', 'No completed trades yet', 'No completed trades in this book yet.', {
        kind: 'empty',
        payload: {}
      })
    ];
  }

  if (n < MIN_TRADES_FOR_LOW_SAMPLE_WARNING) {
    const dates = completedTrades
      .map(getTradeDateString)
      .filter(Boolean)
      .sort();
    out.push(
      buildInsight(
        'low-sample',
        'low_sample',
        'Low sample size',
        `Only ${n} completed trade${n === 1 ? '' : 's'} — treat time patterns as exploratory, not definitive.`,
        {
          kind: 'low_sample',
          payload: {
            completedTrades: n,
            minTradesRecommended: MIN_TRADES_FOR_LOW_SAMPLE_WARNING,
            dateRange: dates.length ? { from: dates[0], to: dates[dates.length - 1] } : null
          }
        }
      )
    );
  }

  const rowsWithTrades = dayRows.filter(r => r.totalTrades > 0);
  if (rowsWithTrades.length === 0) {
    out.push(
      buildInsight(
        'weekday-none',
        'weekday_none',
        'No weekday trades',
        'No weekday (Mon–Fri) trades found for day-of-week analysis.',
        { kind: 'weekday', payload: { rows: [] } }
      )
    );
  } else {
    const bestAmong = rowsWithTrades.reduce((a, b) => (a.totalPnl >= b.totalPnl ? a : b));
    const worstAmong = rowsWithTrades.reduce((a, b) => (a.totalPnl <= b.totalPnl ? a : b));

    out.push(
      buildInsight(
        `weekday-best-${bestAmong.label.toLowerCase()}`,
        'weekday_best',
        'Best weekday',
        `You perform best on ${bestAmong.label}s (${formatRupeePlain(bestAmong.totalPnl)}, ${bestAmong.winRate.toFixed(0)}% win rate).`,
        {
          kind: 'weekday',
          payload: { selected: bestAmong, rows: rowsWithTrades }
        }
      )
    );
    if (worstAmong !== bestAmong) {
      out.push(
        buildInsight(
          `weekday-worst-${worstAmong.label.toLowerCase()}`,
          'weekday_worst',
          'Toughest weekday',
          `${worstAmong.label}s are your toughest day (${formatRupeePlain(worstAmong.totalPnl)}, ${worstAmong.winRate.toFixed(0)}% win rate).`,
          {
            kind: 'weekday',
            payload: { selected: worstAmong, rows: rowsWithTrades }
          }
        )
      );
    }
  }

  if (losingStreakDetails.length >= 2) {
    const k = losingStreakDetails.length;
    out.push(
      buildInsight(
        'losing-streak',
        'losing_streak',
        'Maximum losing streak',
        `You had a maximum losing streak of ${k} trade${k === 1 ? '' : 's'} (consecutive completed trades with negative P&L).`,
        {
          kind: 'losing_streak',
          payload: {
            startIndex: losingStreakDetails.startIndex,
            endIndex: losingStreakDetails.endIndex,
            tradeRefs: losingStreakDetails.tradeRefs
          }
        }
      )
    );
  }

  if (overMeta.overCount > 0) {
    out.push(
      buildInsight(
        'overtrade-days',
        'overtrading_days',
        'Overtrading frequency',
        `You tend to overtrade (${OVERTRADING_THRESHOLD}+ completed round-trips in one day) on ${overMeta.overCount} calendar day${overMeta.overCount === 1 ? '' : 's'}.`,
        {
          kind: 'overtrading_days',
          payload: {
            threshold: OVERTRADING_THRESHOLD,
            overCount: overMeta.overCount,
            overDays: overMeta.overDays
          }
        }
      )
    );
    if (
      overMeta.avgOver != null &&
      overMeta.avgNormal != null &&
      overMeta.normalDaysCount >= 3 &&
      overMeta.overDaysCount >= 1
    ) {
      if (overMeta.avgOver < overMeta.avgNormal) {
        out.push(
          buildInsight(
            'overtrade-impact-worse',
            'overtrading_impact',
            'Overtrading impact',
            `You lose more on high-trade days on average (${formatRupeePlain(overMeta.avgOver)} vs ${formatRupeePlain(overMeta.avgNormal)} on quieter days).`,
            {
              kind: 'overtrading_impact',
              payload: {
                avgOver: overMeta.avgOver,
                avgNormal: overMeta.avgNormal,
                overDaysCount: overMeta.overDaysCount,
                normalDaysCount: overMeta.normalDaysCount
              }
            }
          )
        );
      } else if (overMeta.avgOver > overMeta.avgNormal) {
        out.push(
          buildInsight(
            'overtrade-impact-better',
            'overtrading_impact',
            'Overtrading impact',
            `High-trade days averaged better P&L (${formatRupeePlain(overMeta.avgOver)}) than quieter days (${formatRupeePlain(overMeta.avgNormal)}).`,
            {
              kind: 'overtrading_impact',
              payload: {
                avgOver: overMeta.avgOver,
                avgNormal: overMeta.avgNormal,
                overDaysCount: overMeta.overDaysCount,
                normalDaysCount: overMeta.normalDaysCount
              }
            }
          )
        );
      }
    }
  }

  const nonemptySeg = midSegments.filter(s => s.totalTrades >= MIN_TRADES_PER_SEGMENT_FOR_MID_MONTH);
  if (nonemptySeg.length >= 2) {
    const best = nonemptySeg.reduce((a, b) => (a.totalPnl >= b.totalPnl ? a : b));
    const worst = nonemptySeg.reduce((a, b) => (a.totalPnl <= b.totalPnl ? a : b));
    if (best.totalPnl !== worst.totalPnl) {
      out.push(
        buildInsight(
          `segment-best-${best.key}`,
          'midmonth_strength',
          'Strongest month segment',
          `Your performance is strongest ${best.phraseStrong} (total P&L across all such days).`,
          { kind: 'midmonth', payload: { segments: midSegments, selected: best } }
        )
      );
      if (best.key !== worst.key) {
        out.push(
          buildInsight(
            `segment-worst-${worst.key}`,
            'midmonth_weakness',
            'Weakest month segment',
            `${worst.phraseWeak} shows the weakest total P&L among month segments.`,
            { kind: 'midmonth', payload: { segments: midSegments, selected: worst } }
          )
        );
      }
    }
  } else if (n >= MIN_TRADES_FOR_LOW_SAMPLE_WARNING) {
    out.push(
      buildInsight(
        'segment-uneven',
        'midmonth_uneven',
        'Month segments are uneven',
        'Month segments (days 1–10, 11–20, 21–31) have uneven samples — segment comparison is tentative.',
        { kind: 'midmonth', payload: { segments: midSegments, selected: null } }
      )
    );
  }

  const deduped = [];
  const seen = new Set();
  for (const it of out) {
    if (seen.has(it.summary)) continue;
    seen.add(it.summary);
    deduped.push(it);
  }
  if (deduped.length < 2 && n > 0) {
    deduped.push(
      buildInsight(
        'keep-logging',
        'encouragement',
        'Keep logging',
        'Keep logging trades to sharpen time-based insights.',
        { kind: 'generic', payload: {} }
      )
    );
  }
  return deduped.slice(0, INSIGHT_MAX_COUNT);
}

/** Backward-compatible string summaries */
export function generateTimeBasedInsights(
  completedTrades,
  dayRows,
  maxLosingStreak,
  overMeta,
  midSegments,
  sortedTrades = []
) {
  const losingStreakDetails =
    sortedTrades.length > 0
      ? computeMaxLosingStreakDetails(sortedTrades)
      : { length: maxLosingStreak, startIndex: -1, endIndex: -1, tradeRefs: [] };
  return generateTimeBasedInsightsDetailed(
    completedTrades,
    dayRows,
    losingStreakDetails,
    overMeta,
    midSegments
  ).map(i => i.summary);
}
